import React, { useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { FormattedMessage } from '@artemis/integrations/contentful/utils';
import {
  getIsTeamByIdLoading,
  getTeamLocations,
} from '@artemis/store/teams/selectors';
import TeamSelectionBox from '@artemis/components/Teams/TeamSelectionBox';
import { setAddTeamLocationModalOpen } from '@artemis/store/teams/slice';
import {
  logSelectTeamLocationImpression,
  logSelectTeamLocationSelectLocationClick,
  logSelectTeamLocationNextClick,
  logSelectTeamLocationAddLocationClick,
  logSelectTeamLocationCancelClick,
} from './analytics';
import {
  ButtonContainer,
  Container,
  LinkButton,
  List,
  PrimaryButton,
  SecondaryButton,
  Title,
} from './shared';
import AddLocationModal from './AddLocationModal';

import 'react-loading-skeleton/dist/skeleton.css';

const SelectLocation = ({ teamId, email, onNext, onSkip }) => {
  const dispatch = useDispatch();

  const isLoading = useSelector(getIsTeamByIdLoading);
  const locations = useSelector(getTeamLocations);

  const [selectedLocationId, setSelectedLocationId] = useState();

  const selectedLocation = locations.find(
    loc => loc.locationId === selectedLocationId,
  );

  const disabled = !selectedLocationId;

  const onAddLocation = () => {
    logSelectTeamLocationAddLocationClick({ teamId });
    dispatch(setAddTeamLocationModalOpen(true));
  };

  const onSelectLocation = (locationId, rank) => {
    logSelectTeamLocationSelectLocationClick({
      teamId,
      rank,
      locationId,
    });
    setSelectedLocationId(locationId);
  };

  const onNextClick = () => {
    logSelectTeamLocationNextClick({
      teamId,
      locationId: selectedLocationId,
    });
    onNext(selectedLocation);
  };

  const onSkipClick = () => {
    logSelectTeamLocationCancelClick({
      teamId,
    });
    onSkip();
  };

  useEffectOnce(() => {
    logSelectTeamLocationImpression({ teamId });
  });

  return (
    <>
      <Container>
        <Title>
          <FormattedMessage entry="teams.selectLocation.titleV2" />
        </Title>
        {isLoading ? (
          <Skeleton height={80} borderRadius="6px" />
        ) : (
          <List>
            {locations.map(
              ({ locationId, displayName, secured, memberCount }, index) => (
                <TeamSelectionBox
                  key={locationId}
                  id={locationId}
                  name={displayName}
                  secured={secured}
                  caption={
                    <FormattedMessage
                      entry="teams.memberCount"
                      values={{ count: memberCount }}
                    />
                  }
                  onSelect={id => onSelectLocation(id, index)}
                  selected={selectedLocationId === locationId}
                />
              ),
            )}
          </List>
        )}
        <ButtonContainer>
          <LinkButton onClick={onAddLocation}>
            <FormattedMessage entry="teams.selectLocation.addLocationButton" />
          </LinkButton>
          <PrimaryButton isDisabled={disabled} onClick={onNextClick}>
            <FormattedMessage entry="teams.nextButton" />
          </PrimaryButton>
          {onSkip && (
            <SecondaryButton isDisabled={isLoading} onClick={onSkipClick}>
              <FormattedMessage entry="teams.skipButton" />
            </SecondaryButton>
          )}
        </ButtonContainer>
      </Container>
      <AddLocationModal
        teamId={teamId}
        email={email}
        onLocationConfirmed={location => onNext(location)}
      />
    </>
  );
};

SelectLocation.propTypes = {
  teamId: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
};

export default SelectLocation;
